import React, { useEffect, useState } from "react";
import {
  collection,
  doc,
  getDocs,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { db } from "../../../firebaseConfig";
import {
  calculateOfficeSchemeFee,
  formatINR,
} from "../../../utils/officeScheme/gstCalculator";
import {
  generateInvoiceNo,
  generateReceiptNo,
} from "../../../utils/officeScheme/invoiceNumberHelper";
import { generateGSTInvoicePDF } from "../../../utils/officeScheme/generateGSTInvoicePDF";
import { generateOfficeReceiptPDF } from "../../../utils/officeScheme/generateOfficeReceiptPDF";

export default function OfficePaymentAccounts() {
  const [apps, setApps] = useState([]);
  const [selected, setSelected] = useState(null);
  const [payment, setPayment] = useState({
    utrNo: "",
    bankName: "",
    paymentDate: "",
    remarks: "",
  });

  const fetchApps = async () => {
    const snap = await getDocs(collection(db, "office_scheme_applications"));
    setApps(snap.docs.map((d, index) => ({ id: d.id, seq: index + 1, ...d.data() })));
  };

  useEffect(() => {
    fetchApps();
  }, []);

  const openPayment = (app) => {
    setSelected(app);
    setPayment({
      utrNo: app.utrNo || "",
      bankName: app.bankName || "",
      paymentDate: app.paymentDate || "",
      remarks: app.paymentRemarks || "",
    });
  };

  const markPaid = async () => {
    if (!selected) return;

    if (!payment.utrNo || !payment.bankName || !payment.paymentDate) {
      alert("UTR No, Bank Name aur Payment Date required hai.");
      return;
    }

    const fee = calculateOfficeSchemeFee(selected.estimateAmount);
    const invoiceNo = selected.invoiceNo || generateInvoiceNo(selected.seq);
    const receiptNo = selected.receiptNo || generateReceiptNo(selected.seq);

    await updateDoc(doc(db, "office_scheme_applications", selected.id), {
      consultancyFee: fee.consultancyFee,
      cgst: fee.cgst,
      sgst: fee.sgst,
      totalGst: fee.totalGst,
      invoiceTotal: fee.grandTotal,

      invoiceNo,
      invoiceDate: new Date().toLocaleDateString("en-IN"),
      receiptNo,
      receiptDate: new Date().toLocaleDateString("en-IN"),

      utrNo: payment.utrNo,
      bankName: payment.bankName,
      paymentDate: payment.paymentDate,
      paymentRemarks: payment.remarks,

      paymentStatus: "PAID",
      status: "PAYMENT_DONE",
      paidAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    alert("Payment verified, invoice & receipt details saved.");
    setSelected(null);
    fetchApps();
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl shadow p-6">
        <h2 className="text-2xl font-bold text-slate-800">
          Payment & Accounts
        </h2>
        <p className="text-sm text-slate-500">
          RTGS/NEFT verification, GST invoice, receipt and ledger-style tracking.
        </p>
      </div>

      <SummaryCards apps={apps} />

      <div className="bg-white rounded-2xl shadow p-6 overflow-x-auto">
        <table className="w-full text-sm border">
          <thead className="bg-slate-100">
            <tr>
              <th className="border p-2">Invoice</th>
              <th className="border p-2">Society</th>
              <th className="border p-2">District</th>
              <th className="border p-2">Estimate</th>
              <th className="border p-2">Fee 1.5%</th>
              <th className="border p-2">GST 18%</th>
              <th className="border p-2">Total</th>
              <th className="border p-2">Payment</th>
              <th className="border p-2">UTR</th>
              <th className="border p-2">Action</th>
            </tr>
          </thead>

          <tbody>
            {apps.map((app) => {
              const fee = calculateOfficeSchemeFee(app.estimateAmount);

              return (
                <tr key={app.id}>
                  <td className="border p-2">
                    {app.invoiceNo || generateInvoiceNo(app.seq)}
                  </td>
                  <td className="border p-2 font-bold">
                    {app.societyName || "-"}
                  </td>
                  <td className="border p-2">{app.district || "-"}</td>
                  <td className="border p-2">{formatINR(fee.totalEstimate)}</td>
                  <td className="border p-2">{formatINR(fee.consultancyFee)}</td>
                  <td className="border p-2">{formatINR(fee.totalGst)}</td>
                  <td className="border p-2 font-bold">
                    {formatINR(fee.grandTotal)}
                  </td>
                  <td className="border p-2">
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-bold ${
                        app.paymentStatus === "PAID"
                          ? "bg-emerald-100 text-emerald-700"
                          : "bg-amber-100 text-amber-700"
                      }`}
                    >
                      {app.paymentStatus || "PENDING"}
                    </span>
                  </td>
                  <td className="border p-2">{app.utrNo || "-"}</td>
                  <td className="border p-2">
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => openPayment(app)}
                        className="bg-emerald-600 text-white px-3 py-1 rounded"
                      >
                        Verify
                      </button>

                      <button
                        onClick={() =>
                          generateGSTInvoicePDF({
                            ...app,
                            invoiceNo: app.invoiceNo || generateInvoiceNo(app.seq),
                          })
                        }
                        className="bg-blue-600 text-white px-3 py-1 rounded"
                      >
                        Invoice
                      </button>

                      <button
                        onClick={() => generateOfficeReceiptPDF(app)}
                        className="bg-slate-700 text-white px-3 py-1 rounded"
                      >
                        Receipt
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl max-w-3xl w-full p-6">
            <div className="flex justify-between border-b pb-3 mb-5">
              <div>
                <h2 className="text-2xl font-bold">Payment Verification</h2>
                <p className="text-sm text-slate-500">
                  {selected.societyName} | {selected.district}
                </p>
              </div>

              <button
                onClick={() => setSelected(null)}
                className="bg-slate-200 px-4 py-2 rounded"
              >
                Close
              </button>
            </div>

            <PaymentSummary app={selected} />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
              <Input
                label="UTR / Transaction No"
                value={payment.utrNo}
                onChange={(v) => setPayment((p) => ({ ...p, utrNo: v }))}
              />
              <Input
                label="Bank Name"
                value={payment.bankName}
                onChange={(v) => setPayment((p) => ({ ...p, bankName: v }))}
              />
              <Input
                label="Payment Date"
                type="date"
                value={payment.paymentDate}
                onChange={(v) => setPayment((p) => ({ ...p, paymentDate: v }))}
              />
              <Input
                label="Remarks"
                value={payment.remarks}
                onChange={(v) => setPayment((p) => ({ ...p, remarks: v }))}
              />
            </div>

            <button
              onClick={markPaid}
              className="w-full mt-6 bg-emerald-600 text-white font-bold py-3 rounded-xl"
            >
              Mark Payment Verified & Save Invoice
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryCards({ apps }) {
  const totals = apps.reduce(
    (acc, app) => {
      const fee = calculateOfficeSchemeFee(app.estimateAmount);
      acc.estimate += fee.totalEstimate;
      acc.fee += fee.consultancyFee;
      acc.gst += fee.totalGst;
      acc.total += fee.grandTotal;
      if (app.paymentStatus === "PAID") acc.paid += fee.grandTotal;
      else acc.pending += fee.grandTotal;
      return acc;
    },
    { estimate: 0, fee: 0, gst: 0, total: 0, paid: 0, pending: 0 }
  );

  return (
    <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
      <Card title="Estimate Value" value={formatINR(totals.estimate)} />
      <Card title="Consultancy Fee" value={formatINR(totals.fee)} />
      <Card title="GST" value={formatINR(totals.gst)} />
      <Card title="Invoice Total" value={formatINR(totals.total)} />
      <Card title="Paid" value={formatINR(totals.paid)} />
      <Card title="Pending" value={formatINR(totals.pending)} />
    </div>
  );
}

function PaymentSummary({ app }) {
  const fee = calculateOfficeSchemeFee(app.estimateAmount);

  return (
    <div className="bg-slate-50 border rounded-2xl p-4 grid grid-cols-2 gap-3 text-sm">
      <Info label="Estimate Cost" value={formatINR(fee.totalEstimate)} />
      <Info label="Consultancy Fee 1.5%" value={formatINR(fee.consultancyFee)} />
      <Info label="CGST 9%" value={formatINR(fee.cgst)} />
      <Info label="SGST 9%" value={formatINR(fee.sgst)} />
      <Info label="GST 18%" value={formatINR(fee.totalGst)} />
      <Info label="Total Payable" value={formatINR(fee.grandTotal)} />
    </div>
  );
}

function Card({ title, value }) {
  return (
    <div className="bg-white rounded-2xl shadow p-4">
      <p className="text-xs text-slate-500">{title}</p>
      <h3 className="text-lg font-bold text-slate-800 mt-1">{value}</h3>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <p className="text-slate-500">{label}</p>
      <p className="font-bold">{value}</p>
    </div>
  );
}

function Input({ label, value, onChange, type = "text" }) {
  return (
    <div>
      <label className="text-sm font-bold text-slate-700">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full mt-1 border rounded-xl px-3 py-2"
      />
    </div>
  );
}